import { cn } from '@/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

/**
 * Beautiful glassmorphic modal component with animations.
 * Follows the liquid glass UI design system.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[95vw] h-[90vh]',
  };

  const isFullSize = size === 'full';

  // Render modal using a portal to escape stacking context
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9998]"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              WebkitBackdropFilter: 'blur(4px)',
              backdropFilter: 'blur(4px)',
              willChange: 'opacity',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container — onClick closes when user clicks the empty area
              around the modal box. The container sits at z-9999 ON TOP of the
              backdrop (z-9998), so the backdrop's onClick alone never fires;
              the container has to own the outside-click handler. The motion.div
              below uses stopPropagation so clicks inside the box don't bubble. */}
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={cn(
                // Box itself never scrolls — the inner content wrapper does.
                // This keeps the X button (absolute-positioned on the box)
                // pinned to the corner regardless of content scroll position.
                'relative w-full glass-card overflow-hidden flex flex-col max-h-[90vh]',
                sizeClasses[size]
              )}
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? 'modal-title' : undefined}
              aria-describedby={description ? 'modal-description' : undefined}
            >
              {/* Close Button — z-20 sits above the scrolling content so it
                  remains tappable even when the body scrolls under it. */}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors z-20"
                  aria-label="Close modal"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {/* Header — does not scroll. */}
              {(title || description) && (
                <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 shrink-0">
                  {title && (
                    <h2
                      id="modal-title"
                      className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 pr-8"
                    >
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p
                      id="modal-description"
                      className="text-gray-300 text-xs sm:text-sm"
                    >
                      {description}
                    </p>
                  )}
                </div>
              )}

              {/* Content — the only scrolling element inside the modal. */}
              <div className={cn(
                'flex-1 min-h-0',
                isFullSize ? '' : 'overflow-y-auto',
                !isFullSize && !title && !description && 'px-4 sm:px-6 pt-4 sm:pt-6 pb-4 sm:pb-6',
                !isFullSize && (title || description) && 'px-4 sm:px-6 pb-4 sm:pb-6',
              )}>
                {children}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  // Render to document.body using portal to escape Layout stacking context
  return createPortal(modalContent, document.body);
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => (
  <div className={cn('flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/10', className)}>
    {children}
  </div>
);

interface ModalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  loading?: boolean;
  children: React.ReactNode;
}

export const ModalButton: React.FC<ModalButtonProps> = ({
  variant = 'secondary',
  loading = false,
  disabled,
  children,
  className,
  ...props
}) => {
  const variantClasses = {
    primary: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl',
    secondary: 'glass-button text-white',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl',
  };

  return (
    <button
      className={cn(
        'px-6 py-2.5 rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed',
        variantClasses[variant],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      ) : (
        children
      )}
    </button>
  );
};

